import React, { useState } from 'react';
import { useAppData } from '../../context/Contexts';
import styles from './styles.module.scss';

const VideoPlayer: React.FC = () => {
    const { currentAppData } = useAppData();
    const [playing, setPlaying] = useState(false);

    const { video_url, video_hero_img_url, video_hero_img_title, seat, configurator_complete } = currentAppData ?? {};

    const ready = !!(seat && configurator_complete === true && video_url);
    const noVideo = !!(seat && configurator_complete === true && !video_url);

    // Reset when the selection changes
    React.useEffect(() => {
        setPlaying(false);
    }, [video_url, seat]);

    React.useEffect(() => {
        if (noVideo) {
            window.location.href = 'https://www.seatcoversolutions.com/pages/install-guide';
        }
    }, [noVideo]);

    if (!ready) return null;

    const embedUrl = video_url ? toEmbedUrl(video_url) : null;

    const renderPlayer = () => {
        if (playing && embedUrl) {
            const origin = encodeURIComponent(globalThis.location.origin);
            return (
                <>
                    <iframe
                        src={`${embedUrl}?autoplay=1&rel=0&playsinline=1&origin=${origin}`}
                        title={video_hero_img_title ?? 'Seat cover video'}
                        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                        className={styles.iframe}
                    />
                    <a
                        href={video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.yt_fallback}
                    >
                        ↗ Open in YouTube
                    </a>
                </>
            );
        }
        if (playing && !embedUrl) {
            return (
                <video
                    src={video_url}
                    autoPlay
                    controls
                    poster={video_hero_img_url}
                    className={styles.native_video}
                >
                    <track kind="captions" />
                </video>
            );
        }
        return (
            <button
                type="button"
                className={styles.thumbnail_btn}
                onClick={() => setPlaying(true)}
                aria-label="Play video"
            >
                {video_hero_img_url
                    ? <img src={video_hero_img_url} alt={video_hero_img_title ?? ''} className={styles.thumbnail} />
                    : <div className={styles.thumbnail_placeholder} />
                }
                <span className={styles.overlay}>
                    <span className={styles.play_circle}>
                        <span className={styles.play_triangle} />
                    </span>
                    <span className={styles.watch_label}>Watch Video</span>
                </span>
            </button>
        );
    };

    return (
        <section className={styles.section}>
            {video_hero_img_title && (
                <h3 className={styles.title}>{video_hero_img_title}</h3>
            )}
            <div className={styles.card}>
                {renderPlayer()}
            </div>
        </section>
    );
};

function toEmbedUrl(url: string): string | null {
    const short = /youtu\.be\/([^?&]+)/.exec(url);
    if (short) return `https://www.youtube-nocookie.com/embed/${short[1]}`;
    const long = /youtube\.com\/watch\?.*v=([^&]+)/.exec(url);
    if (long) return `https://www.youtube-nocookie.com/embed/${long[1]}`;
    const vimeo = /vimeo\.com\/(\d+)/.exec(url);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    if (url.includes('/embed/')) return url;
    return null;
}

export default VideoPlayer;
